
import React from 'react';
import TestimonialCard from '@/components/TestimonialCard';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const TestimonialsSection: React.FC = () => {
  const testimonials = [
    {
      quote: "IT Management Solutions completely transformed how our real estate agency handles technology. Their proactive approach has eliminated the constant tech issues we used to face.",
      author: "John D.",
      position: "Property Manager",
      company: "",
      rating: 5.0,
      serviceUsed: "managed-it"
    },
    {
      quote: "As a healthcare provider, data security is our top priority. Their team ensures we're always HIPAA compliant while providing the reliable IT support we need.",
      author: "Sarah M.",
      position: "Director",
      company: "",
      rating: 4.8,
      serviceUsed: "cybersecurity"
    },
    {
      quote: "Their managed IT services have given us peace of mind. We no longer worry about system failures during tax season, and their response time is exceptional.",
      author: "Robert T.",
      position: "Partner",
      company: "",
      rating: 4.9,
      serviceUsed: "cloud-solutions"
    }
  ];

  // Create schema markup for reviews
  const reviewSchemas = testimonials.map((testimonial, index) => ({
    "@context": "https://schema.org/",
    "@type": "Review",
    "itemReviewed": {
      "@type": "LocalBusiness",
      "name": "IT Management Solutions",
      "image": "https://itmgmtsol.com/wp-content/uploads/2025/05/logo1200x630-png.avif",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "24 Ackerman Ave",
        "addressLocality": "Airmont",
        "addressRegion": "NY",
        "postalCode": "10901",
        "addressCountry": "US"
      },
      "telephone": "+1-877-748-6468",
      "url": "https://itmgmtsol.com"
    },
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": testimonial.rating,
      "bestRating": "5"
    },
    "author": {
      "@type": "Person",
      "name": testimonial.author
    },
    "reviewBody": testimonial.quote
  }));

  // Create aggregate rating schema based on all testimonials
  const averageRating = testimonials.reduce((acc, curr) => acc + curr.rating, 0) / testimonials.length;
  
  const aggregateRatingSchema = {
    "@context": "https://schema.org/",
    "@type": "LocalBusiness",
    "name": "IT Management Solutions",
    "image": "https://itmgmtsol.com/wp-content/uploads/2025/05/logo1200x630-png.avif",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "24 Ackerman Ave",
      "addressLocality": "Airmont",
      "addressRegion": "NY",
      "postalCode": "10901",
      "addressCountry": "US"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": averageRating.toFixed(1),
      "reviewCount": testimonials.length,
      "bestRating": "5"
    }
  };

  // Services related to testimonials for internal linking
  const relatedServices = [
    {
      name: "Managed IT Services",
      description: "Comprehensive IT support and monitoring to keep your business running smoothly.",
      link: "/services/managed-it"
    },
    {
      name: "Cybersecurity Solutions",
      description: "Advanced protection against cyber threats and data breaches.",
      link: "/services/cybersecurity"
    },
    {
      name: "Cloud Services",
      description: "Scalable cloud solutions for improved flexibility and productivity.",
      link: "/services/cloud-solutions"
    }
  ];

  return (
    <section className="section-padding bg-white">
      <Helmet>
        {reviewSchemas.map((schema, index) => (
          <script key={`review-schema-${index}`} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
        <script type="application/ld+json">
          {JSON.stringify(aggregateRatingSchema)}
        </script>
      </Helmet>
      
      <div className="container-wide">
        <div className="text-center mb-12">
          <h2 className="heading-lg text-itmgmt-dark-blue mb-4">Client Success Stories</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            See what businesses like yours have to say about our 
            <Link to="/services/managed-it" className="text-itmgmt-bright-blue hover:underline mx-1">managed IT services</Link>
            and
            <Link to="/services/cybersecurity" className="text-itmgmt-bright-blue hover:underline mx-1">cybersecurity solutions</Link>.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              quote={testimonial.quote}
              author={testimonial.author}
              position={testimonial.position}
              company={testimonial.company}
              rating={testimonial.rating}
            />
          ))}
        </div>

        <div className="mt-16">
          <h3 className="text-xl font-bold text-itmgmt-dark-blue mb-6 text-center">Services Our Clients Trust</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedServices.map((service, index) => (
              <div key={index} className="bg-itmgmt-light-gray p-6 rounded-lg">
                <h4 className="text-lg font-bold text-itmgmt-dark-blue mb-2">
                  <Link to={service.link} className="hover:text-itmgmt-bright-blue transition-colors">
                    {service.name}
                  </Link>
                </h4>
                <p className="text-gray-600 mb-3">{service.description}</p>
                <Link to={service.link} className="text-itmgmt-bright-blue hover:underline font-medium">
                  Learn more →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
